import { useState, useEffect, useCallback } from 'react';
import { apiJson } from '../../lib/api';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

const ACTION_COLORS: Record<string, string> = {
  'auth.login': '#8be9fd',
  'org.create': '#4f8fff',
  'org.update': '#bd93f9',
  'org.delete': '#ff5555',
  'org.invite': '#bd93f9',
  'org.member.remove': '#ff5555',
  'invitation.accept': '#4f8fff',
  'user.update': '#8be9fd',
};

interface AuditEntry {
  ts: string;
  actor_github_id: string;
  action: string;
  org_id: string | null;
  target_id: string | null;
  detail: Record<string, unknown>;
}

export function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    apiJson<{ entries: AuditEntry[] }>('/api/admin/audit')
      .then(d => {
        setEntries(d.entries || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) {
    return <div style={{ color: '#6272a4', fontSize: 13, padding: 24, fontFamily: FONT }}>Loading audit log...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#6272a4', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {entries.length} entries (newest first)
        </span>
        <button
          onClick={load}
          style={{
            padding: '6px 16px', background: 'transparent',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4,
            color: '#a8b2b8', fontSize: 11, fontFamily: FONT, cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          Refresh
        </button>
      </div>

      <div style={{
        background: '#1e1e2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Timestamp</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actor</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Org</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Target</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => {
              const actionColor = ACTION_COLORS[e.action] || '#a8b2b8';
              return (
                <tr
                  key={i}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'all 0.15s ease' }}
                  onMouseEnter={ev => (ev.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                  onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '8px 16px', fontSize: 11, color: '#6272a4', whiteSpace: 'nowrap' }}>
                    {new Date(e.ts).toLocaleString()}
                  </td>
                  <td style={{ padding: '8px 16px', fontSize: 11, color: '#a8b2b8' }}>
                    {e.actor_github_id}
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 4,
                      background: `${actionColor}12`, color: actionColor,
                      border: `1px solid ${actionColor}30`,
                      fontFamily: FONT, textTransform: 'uppercase', letterSpacing: '0.03em',
                    }}>
                      {e.action}
                    </span>
                  </td>
                  <td style={{ padding: '8px 16px', fontSize: 11, color: '#6272a4' }}>
                    {e.org_id || '-'}
                  </td>
                  <td style={{ padding: '8px 16px', fontSize: 11, color: '#6272a4' }}>
                    {e.target_id || '-'}
                  </td>
                  <td style={{ padding: '8px 16px', fontSize: 11, color: '#6272a4', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {JSON.stringify(e.detail).substring(0, 80)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {entries.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6272a4', fontSize: 13, fontFamily: FONT }}>
            No audit entries yet.
          </div>
        )}
      </div>
    </div>
  );
}
